import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionForm } from '@/components/transaction/TransactionForm';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { LoadingOverlay } from '@/components/ui/Loading';
import { budgetKeys, useBudgets } from '@/hooks/useBudgets';
import {
  transactionKeys,
  useTransaction,
  useUpdateTransaction,
  useDeleteTransaction,
} from '@/hooks/useTransactions';
import { useAttachTagToTransaction, useDetachTagFromTransaction } from '@/hooks/useTags';
import { TransactionService } from '@/services/TransactionService';
import { transactionApi } from '@/lib/api';
import type { CreateTransactionRequest, UpdateTransactionRequest, ClassificationVerdict } from '@/types';
import { toast } from 'sonner';

type PushFeedbackSession = {
  armed: boolean;
  initialBudgetId: string | null;
  budgetChanged: boolean;
  submitted: boolean;
};

export function TransactionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: transaction, isLoading } = useTransaction(id);

  const classificationHandled = useRef(false);
  useEffect(() => {
    const classification = searchParams.get('classification');
    if (!classification || !transaction || classificationHandled.current) return;
    if (classification !== 'yes' && classification !== 'no') {
      setSearchParams({}, { replace: true });
      return;
    }

    classificationHandled.current = true;
    const verdict: ClassificationVerdict = classification === 'yes' ? 'CORRECT' : 'INCORRECT';

    transactionApi
      .submitClassificationFeedback(transaction.id, { verdict })
      .then(() => {
        toast.success(
          verdict === 'CORRECT'
            ? 'Classification confirmed'
            : 'Marked incorrect — moved to root budget'
        );
        queryClient.invalidateQueries({ queryKey: transactionKeys.all });
        queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      })
      .catch(() => {
        toast.error('Failed to submit classification feedback');
      })
      .finally(() => {
        setSearchParams({}, { replace: true });
      });
  }, [transaction, searchParams, setSearchParams, queryClient]);

  // iOS soft classification: arm session from ?fromPush=1 (no auto-submit on open)
  const pushSession = useRef<PushFeedbackSession>({
    armed: false,
    initialBudgetId: null,
    budgetChanged: false,
    submitted: false,
  });
  const [pushSessionArmed, setPushSessionArmed] = useState(false);
  const pushSessionAlive = useRef(false);

  useEffect(() => {
    const fromPush = searchParams.get('fromPush');
    if (fromPush !== '1' || !transaction || pushSession.current.armed) return;

    pushSession.current = {
      armed: true,
      initialBudgetId: transaction.budgetId,
      budgetChanged: false,
      submitted: false,
    };
    setPushSessionArmed(true);

    const next = new URLSearchParams(searchParams);
    next.delete('fromPush');
    setSearchParams(next, { replace: true });
  }, [transaction, searchParams, setSearchParams]);

  // Leave without budget change → CORRECT (pagehide + deferred unmount for SPA nav / Strict Mode)
  useEffect(() => {
    if (!pushSessionArmed || !id) return undefined;

    pushSessionAlive.current = true;

    const submitCorrectIfNeeded = () => {
      const session = pushSession.current;
      if (!session.armed || session.submitted || session.budgetChanged) return;
      session.submitted = true;
      void transactionApi
        .submitClassificationFeedback(id, { verdict: 'CORRECT' })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: transactionKeys.all });
          queryClient.invalidateQueries({ queryKey: budgetKeys.all });
        })
        .catch(() => {
          // Best-effort; silence must remain valid if this fails
          session.submitted = false;
        });
    };

    const onPageHide = () => submitCorrectIfNeeded();
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      pushSessionAlive.current = false;
      window.setTimeout(() => {
        if (!pushSessionAlive.current) {
          submitCorrectIfNeeded();
        }
      }, 0);
    };
  }, [pushSessionArmed, id, queryClient]);

  const { data: budgets = [] } = useBudgets();
  const updateTransaction = useUpdateTransaction();
  const deleteTransaction = useDeleteTransaction(transaction?.budgetId ?? '');
  const attachTag = useAttachTagToTransaction();
  const detachTag = useDetachTagFromTransaction();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [savingMessage, setSavingMessage] = useState<string | null>(null);
  const selectedTagIdsRef = useRef(selectedTagIds);
  selectedTagIdsRef.current = selectedTagIds;

  const handleTagsChange = (tagIds: string[] | ((prev: string[]) => string[])) => {
    setSelectedTagIds((prev) => {
      const next = typeof tagIds === 'function' ? tagIds(prev) : tagIds;
      selectedTagIdsRef.current = next;
      return next;
    });
  };

  // Only re-sync when opening a different transaction. Do not reset from
  // transaction.tags after update — that would wipe not-yet-attached local tags.
  useEffect(() => {
    const initial = transaction?.tags?.map((tag) => tag.id) ?? [];
    selectedTagIdsRef.current = initial;
    setSelectedTagIds(initial);
  }, [transaction?.id]);

  const handleUpdate = async (
    req: CreateTransactionRequest | UpdateTransactionRequest,
    tagIds: string[]
  ) => {
    if (!transaction) return;

    const updateReq = req as UpdateTransactionRequest;
    const session = pushSession.current;
    const budgetChangedFromPush =
      session.armed
      && !session.submitted
      && Boolean(updateReq.budgetId)
      && updateReq.budgetId !== transaction.budgetId;

    const nextTagIds = new Set(tagIds);
    const currentTagIds = new Set(transaction.tags?.map((tag) => tag.id) ?? []);
    const toAttach = Array.from(nextTagIds).filter((tagId) => !currentTagIds.has(tagId));
    const toDetach = Array.from(currentTagIds).filter((tagId) => !nextTagIds.has(tagId));

    try {
      // Tag attach/detach persists the transaction and bumps @Version.
      // Refresh version afterward before the field update.
      let version = transaction.version ?? 0;

      if (toAttach.length > 0 || toDetach.length > 0) {
        setSavingMessage(
          toAttach.length > 0 && toDetach.length === 0
            ? 'Saving tags…'
            : 'Updating tags…'
        );
        // Attach/detach sequentially: each call bumps transaction @Version.
        for (const tagId of toDetach) {
          await detachTag.mutateAsync({ transactionId: transaction.id, tagId });
        }
        for (const tagId of toAttach) {
          await attachTag.mutateAsync({ transactionId: transaction.id, tagId });
        }
        selectedTagIdsRef.current = tagIds;
        setSelectedTagIds(tagIds);

        const latest = await TransactionService.getById(transaction.id);
        if (!latest) {
          throw new Error('Could not reload transaction after updating tags');
        }
        version = latest.version ?? version;
        queryClient.setQueryData(transactionKeys.detail(transaction.id), latest);
      }

      setSavingMessage('Saving transaction…');
      await updateTransaction.mutateAsync({
        id: transaction.id,
        request: {
          ...updateReq,
          version,
          ...(budgetChangedFromPush ? { fromPushClassification: true } : {}),
        },
      });

      if (budgetChangedFromPush) {
        session.budgetChanged = true;
        session.submitted = true;
        toast.success('Marked incorrect — classification updated');
      }

      navigate(-1);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to save transaction';
      toast.error(message);
      throw error;
    } finally {
      setSavingMessage(null);
    }
  };

  const handleDelete = async () => {
    if (!transaction) return;
    const confirmed = window.confirm('Delete this transaction?');
    if (!confirmed) return;
    // Deleting is not a soft CORRECT confirmation
    pushSession.current.submitted = true;
    await deleteTransaction.mutateAsync(transaction.id);
    navigate(`/budgets/${transaction.budgetId}`);
  };

  if (isLoading || !transaction) {
    return <LoadingOverlay message="Loading transaction..." />;
  }

  const isSaving = savingMessage != null;

  return (
    <div className="space-y-4">
      {isSaving && <LoadingOverlay message={savingMessage} />}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Edit Transaction</CardTitle>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            loading={deleteTransaction.isPending}
            disabled={isSaving}
            aria-label="Delete transaction"
            title="Delete transaction"
            className="px-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              <line x1="10" x2="10" y1="11" y2="17" />
              <line x1="14" x2="14" y1="11" y2="17" />
            </svg>
          </Button>
        </CardHeader>
        <CardContent>
          <TransactionForm
            transaction={transaction}
            budgets={budgets}
            selectedTagIds={selectedTagIds}
            onTagsChange={handleTagsChange}
            onSubmit={handleUpdate}
            onCancel={() => navigate(-1)}
            isLoading={isSaving}
          />
        </CardContent>
      </Card>
    </div>
  );
}
